import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { apiFetch } from '../config/api';
import { formatCHNX } from '../utils/formatCHNX';

interface Tournament {
  matchId: string;
  title: string;
  sideA: string;
  sideB: string;
  matchTime: string;
  venue: string;
  sportType: string;
  status: string;
  totalPool: string;
  activeDuels: number;
  metadata?: {
    source?: string;
    tournament_name?: string;
    location?: string;
    prize_pool?: string;
    category?: string;
    event_format?: string;
  };
}

interface TournamentGroup {
  tournament: string;
  location: string;
  prizePool?: string;
  matches: Tournament[];
  source: string;
}

const Tournaments: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tournaments, setTournaments] = useState<TournamentGroup[]>([]);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'ongoing' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiFetch('/duel/matches?limit=50');
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch tournaments');
      }

      // Group by tournament
      const grouped = groupMatchesByTournament(data.data.matches);
      setTournaments(grouped);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const groupMatchesByTournament = (matches: Tournament[]): TournamentGroup[] => {
    const groups = new Map<string, TournamentGroup>();

    matches.forEach(match => {
      const tournamentName = match.metadata?.tournament_name || match.title;
      const location = match.metadata?.location || match.venue || 'Unknown Location';
      const source = match.metadata?.source || 'manual';
      const prizePool = match.metadata?.prize_pool;

      if (!groups.has(tournamentName)) {
        groups.set(tournamentName, {
          tournament: tournamentName,
          location,
          prizePool,
          matches: [],
          source
        });
      }

      groups.get(tournamentName)!.matches.push(match);
    });

    return Array.from(groups.values()).sort((a, b) => {
      // Sort by source: sportssync > mydupr > manual
      const sourceOrder: Record<string, number> = { sportssync: 0, mydupr: 1, manual: 2 };
      return (sourceOrder[a.source] || 3) - (sourceOrder[b.source] || 3);
    });
  };

  const filteredTournaments = tournaments.filter(tournament => {
    // Status filter
    if (filter !== 'all') {
      const hasMatchingStatus = tournament.matches.some(m => m.status === filter);
      if (!hasMatchingStatus) return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        tournament.tournament.toLowerCase().includes(query) ||
        tournament.location.toLowerCase().includes(query) ||
        tournament.matches.some(m =>
          m.title.toLowerCase().includes(query) ||
          m.sideA.toLowerCase().includes(query) ||
          m.sideB.toLowerCase().includes(query)
        )
      );
    }

    return true;
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'live':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'mydupr':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">🎾 MyDUPR</span>;
      case 'sportssync':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">🏆 SportsSync</span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">📝 Manual</span>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-xl">Loading tournaments...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchTournaments}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🎾 Pickleball Tournaments</h1>
        <p className="mt-2 text-gray-600">Browse and bet on upcoming Pickleball matches</p>
      </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                id="search"
                placeholder="Search tournaments, locations, teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Matches</option>
                <option value="upcoming">Upcoming</option>
                <option value="live">Live</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          {/* Active Filters Display */}
          {(filter !== 'all' || searchQuery) && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {filter !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Status: {filter}
                  <button
                    onClick={() => setFilter('all')}
                    className="ml-1 hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Search: "{searchQuery}"
                  <button
                    onClick={() => setSearchQuery('')}
                    className="ml-1 hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tournament List */}
        {filteredTournaments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500 text-lg">No tournaments found matching your filters</p>
            <button
              onClick={() => {
                setFilter('all');
                setSearchQuery('');
              }}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredTournaments.map((tournament, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Tournament Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h2 className="text-2xl font-bold">{tournament.tournament}</h2>
                        {getSourceBadge(tournament.source)}
                      </div>
                      <div className="flex items-center gap-4 text-blue-100">
                        <span className="flex items-center gap-1">
                          📍 {tournament.location}
                        </span>
                        {tournament.prizePool && (
                          <span className="flex items-center gap-1">
                            💰 {tournament.prizePool}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          🎯 {tournament.matches.length} matches
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Matches List */}
                <div className="divide-y divide-gray-200">
                  {tournament.matches.map((match) => (
                    <Link
                      key={match.matchId}
                      to={`/match/${match.matchId}`}
                      className="block hover:bg-gray-50 transition-colors duration-150"
                    >
                      <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                          {/* Match Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {match.sideA} vs {match.sideB}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(match.status)}`}>
                                {match.status}
                              </span>
                            </div>
                            
                            {match.metadata?.category && (
                              <p className="text-sm text-gray-600 mb-1">
                                {match.metadata.category}
                              </p>
                            )}

                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>🕐 {new Date(match.matchTime).toLocaleString()}</span>
                              <span>📍 {match.venue}</span>
                              {match.metadata?.event_format && (
                                <span>🎾 {match.metadata.event_format}</span>
                              )}
                            </div>
                          </div>

                          {/* Pool Info */}
                          <div className="text-right ml-4">
                            <div className="text-lg font-bold text-indigo-600">
                              {formatCHNX(match.totalPool || '0')} CHNX
                            </div>
                            <div className="text-sm text-gray-500">
                              {match.activeDuels} active duels
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-indigo-600">{tournaments.length}</div>
              <div className="text-sm text-gray-600">Tournaments</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600">
                {tournaments.reduce((sum, t) => sum + t.matches.length, 0)}
              </div>
              <div className="text-sm text-gray-600">Total Matches</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">
                {tournaments.flatMap(t => t.matches).filter(m => m.status === 'upcoming').length}
              </div>
              <div className="text-sm text-gray-600">Upcoming</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600">
                {tournaments.filter(t => t.source !== 'manual').length}
              </div>
              <div className="text-sm text-gray-600">From External APIs</div>
            </div>
          </div>
        </div>
    </Layout>
  );
};

export default Tournaments;

